import { useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { FileListItem } from '@views/documents/elements/FileList'

import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { useFetchFolderContents } from '../../../hooks/useFetchFolderContents'
import { useFetchFolderTree } from '../../../hooks/useFetchFolderTree'
import { useIsClientDocumentsRoute } from '../../../hooks/useIsClientDocumentsRoute'
import { getDocumentsRoutes } from '../../../utils/getDocumentsRoutes'
import { isFolderNotFoundError } from '../../../utils/isFolderNotFoundError'
import { mapDocumentToFileListItem } from '../../../utils/mapDocumentToFileListItem'
import { mapFolderToFileListItem } from '../../../utils/mapFolderToFileListItem'
import { FolderContentParams } from '../types/folderContentParams'

export const useFolderContent = () => {
  const { organization } = useUserOrganization()
  const navigate = useNavigate()
  // react-router-v5-compat types every useParams() value as possibly undefined, unlike v5's
  // useParams<T>() which returned exactly T. The route this hook is used on (see Documents.tsx)
  // requires a non-optional :folderId segment, so a match here always carries a defined value.
  const { folderId } = useParams<FolderContentParams>() as FolderContentParams
  const isClientDocumentsRoute = useIsClientDocumentsRoute()
  const documentsRoutes = getDocumentsRoutes(isClientDocumentsRoute)
  const { tree } = useFetchFolderTree()
  const currentFolderName = tree?.find((n) => n.id === folderId)?.name

  const { documents, folders, isFolderContentsLoading, isError, error } = useFetchFolderContents(folderId, {
    retry: false,
  })

  const isNotFound = isFolderNotFoundError(error)

  const items: FileListItem[] = useMemo(
    () => [...folders.map(mapFolderToFileListItem), ...documents.map(mapDocumentToFileListItem)],
    [folders, documents],
  )

  const isEmpty = items.length === 0

  const filterByName = useCallback(
    (searchQuery: string) => {
      if (!searchQuery) {
        return items
      }
      return items.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    },
    [items],
  )

  const navigateToFolder = useCallback(
    (targetFolderId: string) => {
      const url = getOrganizationViewUrl(organization?.url, documentsRoutes.getFolderRoute(targetFolderId))
      if (url) {
        navigate(url)
      }
    },
    [documentsRoutes, navigate, organization?.url],
  )

  return {
    currentFolderName,
    documents,
    filterByName,
    isEmpty,
    isError,
    isNotFound,
    isLoading: isFolderContentsLoading,
    items,
    navigateToFolder,
  }
}
